/**
 * TD-2422 — canonical role buckets on the FRONTEND.
 *
 * CREFO (stored) key contacts are bucketed on the backend: each contact in the
 * `get-profiler-details` response already carries a `role_bucket` label and the
 * response's `contactsRoleFilterOptions` are the bucketed labels (TD-2330). The
 * FE therefore NEVER reproduces the 25-value CREFO → bucket mapping.
 *
 * Discovered (Surfe) contacts are ephemeral and, by design, out of scope for the
 * backend Key Contacts role filter (TD-2330 header: "Surfe/discovered contacts
 * … are out of scope for this tab"). They arrive with only a Surfe `role`
 * (= seniorities[0] ?? jobTitle → `manager_function`). To let one bucket filter
 * match across BOTH sources (TD-2422) the FE buckets them here, from Surfe's
 * CLOSED 10-value seniority enum.
 *
 * KEEP IN SYNC with td-backend `lambdas-auxbe/lib/roleTaxonomy.js`
 * (ROLE_BUCKETS labels + order, and SURFE_SENIORITY_TO_BUCKET). Only the Surfe
 * half + the labels/order are mirrored — the CREFO map stays backend-only.
 */

// Canonical bucket labels, in canonical order (mirrors roleTaxonomy ROLE_BUCKETS).
// Used to order the union of CREFO (BE-provided) and discovered filter options.
export const ROLE_BUCKET_LABELS = [
  'Managing Director / C-Level',
  'Authorized Signatory',
  'Board / Supervisory',
  'Director / Head',
  'Manager',
  'Shareholder / Owner / Partner',
  'Founder',
  'Other'
];

const OTHER_LABEL = 'Other';

// Surfe seniority (lowercased, closed enum) → canonical bucket label. Mirrors
// roleTaxonomy SURFE_SENIORITY_TO_BUCKET. A free-text jobTitle fallback or any
// unknown value → 'Other' (never silently dropped).
const SURFE_SENIORITY_TO_LABEL = {
  'board member': 'Board / Supervisory',
  'c-level': 'Managing Director / C-Level',
  director: 'Director / Head',
  founder: 'Founder',
  head: 'Director / Head',
  manager: 'Manager',
  other: OTHER_LABEL,
  owner: 'Shareholder / Owner / Partner',
  partner: 'Shareholder / Owner / Partner',
  vp: 'Director / Head'
};

/**
 * Bucket a discovered contact's Surfe seniority (its `role` / `manager_function`)
 * to a canonical bucket label. Unknown / absent → 'Other'.
 * @param {string|null|undefined} value
 * @returns {string} a ROLE_BUCKET_LABELS value
 */
export const bucketLabelForSurfeSeniority = (value) => {
  if (typeof value !== 'string') return OTHER_LABEL;
  return SURFE_SENIORITY_TO_LABEL[value.trim().toLowerCase()] ?? OTHER_LABEL;
};

/**
 * Order a collection of bucket labels by canonical order. Labels not in the
 * canonical set (defensive — e.g. a future BE bucket the FE hasn't mirrored yet)
 * are appended, de-duplicated, in first-seen order so nothing is dropped.
 * @param {Iterable<string>} labels
 * @returns {string[]} canonical-ordered, de-duplicated labels
 */
export const orderRoleBucketLabels = (labels) => {
  const present = new Set(labels);
  const ordered = ROLE_BUCKET_LABELS.filter((label) => present.has(label));
  const extras = [...present].filter((label) => !ROLE_BUCKET_LABELS.includes(label));
  return [...ordered, ...extras];
};
